import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Search,
  FilterList,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Timesheet {
  _id: string;
  employee: {
    firstName: string;
    lastName: string;
  };
  project: {
    name: string;
  };
  date: string;
  totalHours?: number;
  totalOvertimeHours?: number;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';
  description: string;
  submittedAt?: string;
  approvedAt?: string;
}

const Timesheets: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTimesheet, setSelectedTimesheet] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchTimesheets = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/timesheets');
        const data = response.data?.data || [];
        setTimesheets(Array.isArray(data) ? data : []);
      } catch (err: any) {
        console.error('Error fetching timesheets:', err);
        setError(err.response?.data?.message || 'Failed to fetch timesheets');
      } finally {
        setLoading(false);
      }
    };

    fetchTimesheets();
  }, []);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, timesheetId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedTimesheet(timesheetId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTimesheet(null);
  };

  const handleViewDetails = () => {
    if (selectedTimesheet) {
      navigate(`/timesheets/${selectedTimesheet}`);
    }
    handleMenuClose();
  };

  const handleEditTimesheet = () => {
    if (selectedTimesheet) {
      // Navigate to detail for now; edit flow lives there
      navigate(`/timesheets/${selectedTimesheet}`);
    }
    handleMenuClose();
  };

  const handleApproveTimesheet = async () => {
    if (!selectedTimesheet) return;
    const selectedTs = timesheets.find(ts => ts._id === selectedTimesheet);
    if (selectedTs && selectedTs.status !== 'submitted') {
      setError('Submit the timesheet before approving or rejecting.');
      handleMenuClose();
      return;
    }
    try {
      setActionLoading(true);
      await api.post(`/timesheets/${selectedTimesheet}/approve`, { comments: '' });
      setTimesheets((prev) => prev.map((ts) => ts._id === selectedTimesheet ? { ...ts, status: 'approved' } : ts));
    } catch (err: any) {
      console.error('Approve error:', err);
      setError(err.response?.data?.message || 'Failed to approve timesheet');
    } finally {
      setActionLoading(false);
      handleMenuClose();
    }
  };

  const handleRejectTimesheet = async () => {
    if (!selectedTimesheet) return;
    const selectedTs = timesheets.find(ts => ts._id === selectedTimesheet);
    if (selectedTs && selectedTs.status !== 'submitted') {
      setError('Submit the timesheet before approving or rejecting.');
      handleMenuClose();
      return;
    }
    try {
      setActionLoading(true);
      await api.post(`/timesheets/${selectedTimesheet}/reject`, { reason: rejectReason || 'Not specified' });
      setTimesheets((prev) => prev.map((ts) => ts._id === selectedTimesheet ? { ...ts, status: 'rejected' } : ts));
      setRejectOpen(false);
      setRejectReason('');
    } catch (err: any) {
      console.error('Reject error:', err);
      setError(err.response?.data?.message || 'Failed to reject timesheet');
    } finally {
      setActionLoading(false);
      handleMenuClose();
    }
  };

  const handleDownloadPdf = () => {
    if (selectedTimesheet) {
      // Navigate to detail where Export/Print lives
      navigate(`/timesheets/${selectedTimesheet}`);
    }
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'submitted':
        return 'warning';
      case 'draft':
        return 'default';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredTimesheets = timesheets.filter(
    (timesheet) =>
      timesheet.employee.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      timesheet.employee.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      timesheet.project.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          Timesheets
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ borderRadius: 2 }}
          onClick={() => navigate('/timesheets/create')}
        >
          New Timesheet
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder="Search timesheets..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              sx={{ borderRadius: 2 }}
            >
              Filter
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell>Overtime</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2">Loading timesheets...</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && error && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="error">{error}</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && !error && filteredTimesheets.map((timesheet) => (
                <TableRow 
                  key={timesheet._id} 
                  hover 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/timesheets/${timesheet._id}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {timesheet.employee.firstName} {timesheet.employee.lastName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {timesheet.project.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(timesheet.date).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {(timesheet.totalHours ?? 0)}h
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={(timesheet.totalOvertimeHours ?? 0) > 0 ? 'warning.main' : 'text.secondary'}>
                      {(timesheet.totalOvertimeHours ?? 0)}h
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={timesheet.status.charAt(0).toUpperCase() + timesheet.status.slice(1)}
                      color={getStatusColor(timesheet.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {timesheet.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClick(e, timesheet._id);
                      }}
                      size="small"
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {(() => {
          const selectedTs = timesheets.find(ts => ts._id === selectedTimesheet);
          const isSubmitted = selectedTs?.status === 'submitted';
          const isEditable = selectedTs && selectedTs.status !== 'approved' && selectedTs.status !== 'paid';

          const items: React.ReactElement[] = [
            <MenuItem key="view" onClick={handleViewDetails}>View Details</MenuItem>,
            <MenuItem key="edit" disabled={!isEditable} onClick={handleEditTimesheet}>Edit Timesheet</MenuItem>,
            <MenuItem key="approve" disabled={!isSubmitted || actionLoading} onClick={handleApproveTimesheet}>Approve</MenuItem>,
            <MenuItem key="reject" disabled={!isSubmitted || actionLoading} onClick={() => { setRejectOpen(true); }}>
              Reject
            </MenuItem>,
          ];

          if (!isSubmitted) {
            items.push(
              <MenuItem key="hint-submit" disabled>
                <Typography variant="caption" color="text.secondary">
                  Submit the timesheet before approving or rejecting.
                </Typography>
              </MenuItem>
            );
          }

          if (!isEditable) {
            items.push(
              <MenuItem key="hint-edit" disabled>
                <Typography variant="caption" color="text.secondary">
                  Editing is disabled for approved/paid timesheets.
                </Typography>
              </MenuItem>
            );
          }

          return items;
        })()}
        <MenuItem onClick={handleDownloadPdf}>Download PDF</MenuItem>
      </Menu>

      {rejectOpen && (
        <Card sx={{ position: 'fixed', bottom: 24, right: 24, p: 2, width: 360, boxShadow: 6 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Reject Timesheet
          </Typography>
          <TextField
            label="Reason"
            placeholder="Enter rejection reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="text" onClick={() => { setRejectOpen(false); setRejectReason(''); }}>
              Cancel
            </Button>
            <Button variant="contained" disabled={actionLoading || !rejectReason.trim()} onClick={handleRejectTimesheet}>
              Confirm Reject
            </Button>
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default Timesheets;